import type { RangeAttributeSettingConfiguration } from '../../types/attribute-settings/attribute-settings-config.zod';

export type AttributeRangeSettingDataPayload = {
    schemaName: string;
    minValue: RangeAttributeSettingConfiguration['min']['value'];
    maxValue: RangeAttributeSettingConfiguration['max']['value'];
};

export type AttributeLimitSettingDataPayload = {
    amount: number;
    operator: string;
    schemaName: string;
};

type SchemaStatusType = 'warning' | 'error' | 'success';

export type InterpolateData = {
    name: string;
    type: string;
    value: string;
};

type BaseSchemaStatus = {
    icon: string;
    message: string;
    type: SchemaStatusType;
    messagePlaintext: string;
    interpolates: InterpolateData[];
}

interface SchemaWarning extends BaseSchemaStatus {
    type: 'warning';
    icon: 'mdi-alert-outline';
}

interface SchemaError extends BaseSchemaStatus {
    type: 'error';
    icon: 'mdi-alert-octagon-outline';
}

interface SchemaSuccess extends BaseSchemaStatus {
    type: 'success';
    icon: 'mdi-check';
}

export type SchemaStatus = SchemaWarning | SchemaError | SchemaSuccess;

export const rangeInterpolates = (payload: AttributeRangeSettingDataPayload): InterpolateData[] => [
    {
        type: 'b',
        name: 'suggests',
        value: 'suggests',
    },
    {
        type: 'b',
        name: 'requires',
        value: 'requires',
    },
    {
        name: 'min',
        type: 'code',
        value: payload.minValue === null ? 'null' : payload.minValue.toString(),
    },
    {
        name: 'max',
        type: 'code',
        value: payload.maxValue === null ? 'null' : payload.maxValue.toString(),
    },
    {
        type: 'code',
        name: 'schemaName',
        value: payload.schemaName,
    },
];

export const generateWarningRangeStatus = (payload: AttributeRangeSettingDataPayload): SchemaStatus => ({
    type: 'warning',
    icon: 'mdi-alert-outline',
    interpolates: rangeInterpolates(payload),
    message: 'Setting {suggests} range of at least {min} and at most {max} attributes from the {schemaName} schema to be linked',
    messagePlaintext: `Setting suggests range of [${payload.minValue}, ${payload.maxValue}] attributes from the ${payload.schemaName} schema to be linked`,
});

export const generateRequiredRangeStatus = (payload: AttributeRangeSettingDataPayload): SchemaStatus => ({
    type: 'error',
    icon: 'mdi-alert-octagon-outline',
    interpolates: rangeInterpolates(payload),
    message: 'Setting {requires} range of at least {min} and at most {max} attributes from the {schemaName} schema to be linked',
    messagePlaintext: `Setting suggests range of [${payload.minValue}, ${payload.maxValue}] attributes from the ${payload.schemaName} schema to be linked`,
});
